// US-16 — one place prose/description cells clamp instead of hard-truncating
// mid-word. Replaces scattered `line-clamp-2 break-words` className combos so
// dense list/table cells wrap to N lines and never cut a word off. The full
// text stays available on hover via the native title tooltip.
import React from 'react';
import { cn } from '@/lib/utils';

// Tailwind needs the class names to appear literally (no dynamic interpolation),
// so map the supported line counts to static classes.
const CLAMP: Record<number, string> = {
  1: 'line-clamp-1',
  2: 'line-clamp-2',
  3: 'line-clamp-3',
};

export interface TruncatedTextProps {
  children?: React.ReactNode;
  /** Number of lines before clamping (1–3). Default 2. */
  lines?: 1 | 2 | 3;
  /** Extra classes (size, color, max-width) merged after the clamp defaults. */
  className?: string;
  /** Render element. Default 'div'. */
  as?: 'div' | 'p' | 'span';
  /** Tooltip text; defaults to the string children so the full value is reachable. */
  title?: string;
}

/**
 * Multi-line clamp for descriptions/summaries in dense lists. Wraps and breaks
 * long words rather than truncating mid-content.
 */
export const TruncatedText: React.FC<TruncatedTextProps> = ({
  children,
  lines = 2,
  className,
  as: Tag = 'div',
  title,
}) => {
  const tip = title ?? (typeof children === 'string' ? children : undefined);
  return (
    <Tag className={cn(CLAMP[lines], 'break-words', className)} title={tip}>
      {children}
    </Tag>
  );
};

export default TruncatedText;
